import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChildren, QueryList, ElementRef, HostListener } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { DepositService } from 'src/app/services/deposit/deposit.service';
import { NonPaginatedResponse, PaginatedResponse } from 'src/app/shared/models';

@Component({
  selector: 'app-deposit-listing',
  templateUrl: './deposit-listing.component.html',
  styleUrls: ['./deposit-listing.component.scss']
})
export class DepositListingComponent extends DestructorComponent implements OnInit, OnChanges {
  deposits: any[] = [];
  @Input() customerId: string | null = null;
  @Output() depositSelected = new EventEmitter<any>();
  @ViewChildren('tooltipPopup') tooltipPopups!: QueryList<ElementRef>;

  selectedDeposit: any = null;
  currentTooltip: HTMLElement | null = null;
  isLoading: boolean = false;
  constructor(private _depositService: DepositService) { super(); }

  selectDeposit(deposit: any): void {
    this.selectedDeposit = deposit;
    this.depositSelected.emit(deposit);
  }

  isSelected(deposit: any): boolean {
    return this.selectedDeposit && this.selectedDeposit._id === deposit._id;
  }

  ngOnInit(): void {
    if (this.customerId) {
      this.GetData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId'] && !changes['customerId'].firstChange) {
      this.GetData();
    }
  }

  toggleTooltip(event: MouseEvent, _deposit: any): void {
    // Stop event propagation to prevent row selection
    event.stopPropagation();

    const target = event.currentTarget as HTMLElement;
    const tooltip = target.querySelector('.deposit-details-popup') as HTMLElement;

    if (!tooltip) return;

    // If this tooltip is already open, close it
    if (this.currentTooltip === tooltip) {
      this.hideTooltip();
      return;
    }

    // Hide any existing tooltip
    this.hideTooltip();

    // Show the tooltip
    tooltip.style.display = 'block';
    this.currentTooltip = tooltip;

    // Position the tooltip
    this.positionTooltip(target, tooltip);
  }

  hideTooltip(): void {
    if (this.currentTooltip) {
      this.currentTooltip.style.display = 'none';
      this.currentTooltip = null;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.currentTooltip) {
      const target = event.target as HTMLElement;
      // Check if click is outside the popup and outside the trigger
      const isClickInsidePopup = this.currentTooltip.contains(target);
      const isClickInsideTrigger = target.closest('.deposit-hover-container') !== null;

      if (!isClickInsidePopup && !isClickInsideTrigger) {
        this.hideTooltip();
      }
    }
  }

  closeTooltip(event?: MouseEvent): void {
    // Stop event propagation to prevent row selection
    if (event) {
      event.stopPropagation();
    }
    this.hideTooltip();
  }

  private positionTooltip(trigger: HTMLElement, tooltip: HTMLElement): void {
    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    let top = triggerRect.bottom + scrollTop + 8;
    let left = triggerRect.left + scrollLeft + (triggerRect.width / 2) - (tooltipRect.width / 2);

    // Adjust horizontal position if tooltip goes outside viewport
    if (left < scrollLeft + 10) {
      left = scrollLeft + 10;
    } else if (left + tooltipRect.width > scrollLeft + viewportWidth - 10) {
      left = scrollLeft + viewportWidth - tooltipRect.width - 10;
    }

    // Adjust vertical position if tooltip goes outside viewport
    if (top + tooltipRect.height > scrollTop + viewportHeight - 10) {
      // Show above the trigger
      top = triggerRect.top + scrollTop - tooltipRect.height - 8;
    }

    // Ensure tooltip doesn't go above the viewport
    if (top < scrollTop + 10) {
      top = scrollTop + 10;
    }

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
  }

  GetData() {
    this.isLoading = true;
    this.subscriptions.push(
      this._depositService.GetData("1", "", { paginate: false, customer_id: this.customerId }).subscribe({
        next: (response: NonPaginatedResponse<any>) => {
          console.log('API Response:', response); // Debug log
          if (response.status && response.data) {
            this.deposits = response.data || [];
          } else {
            this.deposits = [];
            console.log('No data found in response'); // Debug log
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading deposits:', error);
          this.deposits = [];
          this.isLoading = false;
        }
      })
    );
  }

  // Calculate total received amount
  getTotalAmount(): number {
    return this.deposits.reduce((sum, deposit) => {
      return sum + (deposit.received_amount || 0);
    }, 0);
  }

  // Calculate total allocated amount
  getTotalAllocated(): number {
    return this.deposits.reduce((sum, deposit) => {
      return sum + (deposit.amount_allocated || 0);
    }, 0);
  }

  // Calculate total available amount
  getTotalAvailable(): number {
    return this.deposits.reduce((sum, deposit) => {
      return sum + (deposit.remaining_amount || 0);
    }, 0);
  }

  // Get currency symbol based on customer currency type
  getCurrencySymbol(): string {
    if (this.deposits.length > 0 && this.deposits[0].customer) {
      return this.deposits[0].customer.currency_type === 'usd' ? '$' : '¥';
    }
    return '¥'; // Default to JPY
  }
}
